import React from 'react';

interface HighlightedTextProps {
  children: string | React.ReactNode;
  markClassName?: string;
  boldClassName?: string;
}

type Part = string | { type: 'mark' | 'bold'; content: string };

function parseHighlightedText(raw: string): Part[] {
  const tokenRe = /(<mark[^>]*>.*?<\/mark>|\*\*.*?\*\*)/g;
  const parts: Part[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenRe.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      parts.push(raw.slice(lastIndex, match.index));
    }

    const token = match[0];

    if (token.startsWith('<mark')) {
      const content = token.replace(/<mark[^>]*>(.*?)<\/mark>/, '$1');
      parts.push({ type: 'mark', content });
    } else {
      parts.push({ type: 'bold', content: token.slice(2, -2) });
    }

    lastIndex = match.index + token.length;
  }

  if (lastIndex < raw.length) {
    parts.push(raw.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [raw];
}

export const HighlightedText = ({
  children,
  markClassName = 'bg-blue-100 rounded-[2px] text-black',
  boldClassName = 'font-medium',
}: HighlightedTextProps) => {
  if (typeof children !== 'string') {
    return <>{children}</>;
  }

  const parts = parseHighlightedText(children);

  return (
    <span>
      {parts.map((part, index) => {
        if (typeof part === 'string') {
          return <React.Fragment key={index}>{part}</React.Fragment>;
        }
        if (part.type === 'bold') {
          return (
            <span key={index} className={boldClassName}>
              {part.content}
            </span>
          );
        }
        return (
          <span key={index} className={markClassName}>
            {part.content}
          </span>
        );
      })}
    </span>
  );
};
